import React from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { GoogleReviews } from '../components/GoogleReviews';
import { ArrowRight, Calendar, MessageCircle, Microscope, Scan, MonitorPlay, ShieldCheck, Languages, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
export function AboutPage() {
  const {
    t,
    lang
  } = useTranslation();
  const advantages = [{
    icon: Microscope,
    title: lang === 'en' ? 'Dental Microscope' : lang === 'ua' ? 'Стоматологічний мікроскоп' : lang === 'ro' ? 'Microscop Dentar' : 'Стоматологический микроскоп',
    desc: lang === 'en' ? 'Higher precision for root canals' : lang === 'ua' ? 'Вища точність лікування каналів' : lang === 'ro' ? 'Precizie mai mare pentru tratamentele de canal' : 'Высокая точность лечения каналов'
  }, {
    icon: Scan,
    title: lang === 'en' ? '3D Digital Scanner' : lang === 'ua' ? 'Цифровий 3D сканер' : lang === 'ro' ? 'Scanner Digital 3D' : 'Цифровой 3D сканер',
    desc: lang === 'en' ? 'No messy impressions needed' : lang === 'ua' ? 'Без відбитків — миттєвий результат' : lang === 'ro' ? 'Fără amprente dezordonate' : 'Без слепков — мгновенный результат'
  }, {
    icon: MonitorPlay,
    title: lang === 'en' ? 'Digital Smile Design' : lang === 'ua' ? 'Цифровий дизайн посмішки' : lang === 'ro' ? 'Design Digital al Zâmbetului' : 'Цифровой дизайн улыбки',
    desc: lang === 'en' ? 'See your result before treatment' : lang === 'ua' ? 'Побачте результат до лікування' : lang === 'ro' ? 'Vedeți rezultatul înainte de tratament' : 'Увидьте результат до лечения'
  }, {
    icon: ShieldCheck,
    title: lang === 'en' ? 'UK Sterilisation Standards' : lang === 'ua' ? 'Стандарти стерилізації UK' : lang === 'ro' ? 'Standarde de Sterilizare UK' : 'Стандарты стерилизации UK',
    desc: lang === 'en' ? 'HTM 01-05 — safety you can trust' : lang === 'ua' ? 'HTM 01-05 — безпека, якій довіряють' : lang === 'ro' ? 'HTM 01-05 — siguranță de încredere' : 'HTM 01-05 — безопасность, которой доверяют'
  }, {
    icon: Languages,
    title: lang === 'en' ? 'Multilingual Team' : lang === 'ua' ? 'Багатомовна команда' : lang === 'ro' ? 'Echipă Multilingvă' : 'Многоязычная команда',
    desc: lang === 'en' ? 'English · Ukrainian · Russian' : lang === 'ua' ? 'Українська · English · Російська' : lang === 'ro' ? 'Engleză · Ucraineană · Rusă' : 'Русский · English · Українська'
  }, {
    icon: CreditCard,
    title: lang === 'en' ? 'Transparent Pricing' : lang === 'ua' ? 'Прозорі ціни' : lang === 'ro' ? 'Prețuri Transparente' : 'Прозрачные цены',
    desc: lang === 'en' ? 'No hidden fees — ever' : lang === 'ua' ? 'Жодних прихованих платежів' : lang === 'ro' ? 'Fără taxe ascunse — niciodată' : 'Никаких скрытых платежей'
  }];
  return <Layout data-id="element-389">
      {/* Hero with image */}
      <section className="relative" data-id="element-390">
        <div className="h-[280px] sm:h-[360px] lg:h-[420px] w-full overflow-hidden" data-id="element-391">
          <img src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1200&q=80" alt="Dentico Clinic" className="w-full h-full object-cover" data-id="element-392" />
          <div className="absolute inset-0 bg-gradient-to-t from-teal-900/80 via-teal-900/40 to-teal-900/20" data-id="element-393" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12" data-id="element-394">
          <div className="max-w-7xl mx-auto text-center" data-id="element-395">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3" data-id="element-396">
              {t('about.title')}
            </h1>
            <p className="text-lg text-teal-100 max-w-xl mx-auto" data-id="element-397">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section - shorter text */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-398">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-399">
          <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-400">
            <div className="relative" data-id="element-401">
              <div className="absolute inset-0 bg-teal-100 rounded-3xl transform -rotate-3" data-id="element-402"></div>
              <img src="https://images.unsplash.com/photo-1629909615184-74f495363b67?auto=format&fit=crop&w=800&q=80" alt="Our Clinic Interior" className="relative rounded-3xl shadow-xl w-full h-[320px] lg:h-[420px] object-cover" data-id="element-403" />
            </div>
            <div className="space-y-5" data-id="element-404">
              <h2 className="text-3xl font-bold text-gray-900" data-id="element-405">
                {t('about.storyTitle')}
              </h2>
              <p className="text-gray-600 leading-relaxed" data-id="element-406">
                {t('about.storyP1')}
              </p>
              <p className="text-gray-600 leading-relaxed" data-id="element-407">
                {t('about.storyP2')}
              </p>
              <div className="pt-2" data-id="element-408">
                <Link to={`/${lang}/contact`} data-id="element-409">
                  <Button size="lg" className="gap-2" data-id="element-410">
                    {t('about.meetTeam')} <ArrowRight className="w-4 h-4" data-id="element-411" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Advantages */}
      <section className="py-16 lg:py-20 bg-gray-50" data-id="element-412">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-413">
          <div className="text-center mb-12" data-id="element-414">
            <h2 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-415">
              {lang === 'en' ? 'Advanced Technology & Care' : lang === 'ua' ? 'Передові технології та турбота' : lang === 'ro' ? 'Tehnologie Avansată și Îngrijire' : 'Передовые технологии и забота'}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-416">
            {advantages.map((adv, idx) => <div key={idx} className="bg-white p-5 lg:p-6 rounded-2xl border border-gray-100 flex flex-col items-center text-center" data-id="element-417">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4" data-id="element-418">
                  <adv.icon className="w-6 h-6" data-id="element-419" />
                </div>
                <h3 className="text-sm lg:text-base font-bold text-gray-900 mb-1" data-id="element-420">
                  {adv.title}
                </h3>
                <p className="text-gray-500 text-xs lg:text-sm" data-id="element-421">{adv.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <GoogleReviews data-id="element-422" />

      {/* CTA */}
      <section className="py-16 bg-teal-900" data-id="element-423">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-424">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-6" data-id="element-425">
            {t('about.ctaTitle')}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-426">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-427">
              <Button size="lg" className="gap-2 w-full sm:w-auto bg-white text-teal-900 hover:bg-teal-50" data-id="element-428">
                <Calendar className="w-5 h-5" data-id="element-429" />
                {t('nav.bookOnline')}
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-430">
              <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-431">
                <MessageCircle className="w-5 h-5" data-id="element-432" />
                WhatsApp
              </Button>
            </a>
            <Link to={`/${lang}/contact`} data-id="element-433">
              <Button variant="outline" size="lg" className="gap-2 w-full sm:w-auto border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-434">
                {t('nav.contact')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>;
}